import React from 'react'
import { Button, Flex, Popconfirm, Table, TableProps, Tag } from 'antd'
import { useTranslation } from 'react-i18next'
import { DeleteOutlined } from '@ant-design/icons'
import { PendingInvitation } from '../invitation'
import { MailLink } from '../../components/mail-link'
import { TimeAgo } from '../../components/time-ago'
import { useRevokeInvitation } from '../hooks'
import { useAuth } from '../../auth'
import { DELETE_INVITATION } from '../../user/permission'

export type InvitationsTableProps = {
    companyId?: string
} & TableProps<PendingInvitation>

export function InvitationsTable({ companyId, ...props }: InvitationsTableProps) {
    const { t } = useTranslation()
    const { can } = useAuth()

    const rows = (props.dataSource as PendingInvitation[] | undefined) ?? []
    const showCampaigns = rows.some((invitation) => invitation.assignedCampaigns.length > 0)

    return (
        <Table
            size="small"
            rowKey="email"
            locale={{ emptyText: t('No pending invitations') }}
            columns={[
                { title: t('Email'), dataIndex: 'email', render: (email) => <MailLink email={email} /> },
                {
                    width: 400,
                    title: t('Assigned campaigns'),
                    key: 'assigned_campaigns',
                    hidden: !showCampaigns,
                    render: (_, record) => (
                        <Flex
                            gap="small"
                            wrap
                        >
                            {record.assignedCampaigns.map((campaign) => (
                                <Tag key={campaign.id}>{campaign.title}</Tag>
                            ))}
                        </Flex>
                    ),
                },
                {
                    title: t('Sent'),
                    dataIndex: 'createdAt',
                    width: 160,
                    render: (date) => <TimeAgo date={date} />,
                },
                {
                    key: 'action',
                    width: 50,
                    hidden: !can(DELETE_INVITATION),
                    render: (_, record) => (
                        <Flex justify="end">
                            <RevokeInvitationButton
                                companyId={companyId}
                                email={record.email}
                            />
                        </Flex>
                    ),
                },
            ]}
            {...props}
        />
    )
}

function RevokeInvitationButton({ companyId, email }: { companyId?: string; email: string }) {
    const { t } = useTranslation()
    const mutation = useRevokeInvitation(companyId, email)

    return (
        <Popconfirm
            title={t('Are you sure?')}
            onConfirm={() => mutation.mutate()}
        >
            <Button
                type="text"
                loading={mutation.isPending}
                icon={<DeleteOutlined />}
            />
        </Popconfirm>
    )
}
